import { Environment } from '../utils/Environment';

const apiUrl = Environment.getApiUrl();

export async function login(username: string, password: string): Promise<string> {
    const response = await fetch(`${apiUrl}/login`, {
        method: "POST",
        headers: {
            "Content-Type": "application/x-www-form-urlencoded",
        },
        body: new URLSearchParams({ username, password }).toString(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Failed to log in"); // Use the generic error message from the backend
    }

    const data = await response.json();
    return data.access_token; // Return the JWT token
}

export const handleLogin = async (username: string, password: string): Promise<void> => {
  try {
    const response = await fetch(`${apiUrl}/login`, {
      method: "POST",
      headers: {
        "Content-Type": "application/x-www-form-urlencoded",
      },
      body: new URLSearchParams({ username, password }).toString(),
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || "Login failed");
    }

    const { access_token, refresh_token } = await response.json();

    // Store tokens in localStorage
    localStorage.setItem("access_token", access_token);
    localStorage.setItem("refresh_token", refresh_token);

    alert("Login successful!");
  } catch (err) {
    console.error("Login failed:", err);
    throw err; // Rethrow the error for the caller to handle
  }
};

export async function refreshToken(refresh_token: string): Promise<string> {
  if (!refresh_token) {
    throw new Error("No refresh token provided");
  }

  try {
    const response = await fetch(`${apiUrl}/refresh-token`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ refresh_token }),
    });

    if (!response.ok) {
      throw new Error("Failed to refresh token");
    }

    const data = await response.json();
    if (!data.access_token) {
      throw new Error("No access token returned from refresh");
    }
    return data.access_token;
  } catch (err) {
    console.error("Failed to refresh token:", err);
    throw err;
  }
}

export function isTokenExpired(token: string): boolean {
  try {
    const payload = JSON.parse(atob(token.split(".")[1])); // Decode the JWT payload
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    return payload.exp < currentTime; // Compare expiration time with current time
  } catch (error) {
    console.error("Failed to parse token:", error);
    return true; // Treat invalid tokens as expired
  }
}

export async function validateToken(): Promise<boolean> {
  let access_token = localStorage.getItem("access_token");
  if (!access_token) {
    return false;
  }

  try {
    const response = await fetch(`${apiUrl}/validate-token`, {
      method: "GET",
      headers: {
        Authorization: `Bearer ${access_token}`,
      },
    });

    if (response.ok) {
      const data = await response.json();
      return data.status === "valid";
    }

    // If token is invalid/expired, try to refresh
    if (response.status === 401) {
      const refresh_token = localStorage.getItem("refresh_token");
      try {
        const newAccessToken = await refreshToken(refresh_token ?? "");
        localStorage.setItem("access_token", newAccessToken);

        // Retry validation with new token
        const retryResponse = await fetch(`${apiUrl}/validate-token`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${newAccessToken}`,
          },
        });
        if (retryResponse.ok) {
          const data = await retryResponse.json();
          return data.status === "valid";
        }
      } catch (refreshErr) {
        // Refresh failed, treat as invalid
        return false;
      }
    }
    // Other errors
    return false;
  } catch (error) {
    console.error("Token validation failed:", error);
    return false;
  }
}